import type { Atom, WritableAtom } from 'jotai';
import { atom } from 'jotai';

import { directoriesQueryAtom } from '../_graphql/queries/directoriesQuery';
import { FileRecord } from '../types/files';

import { maybeParseZonedDateTime } from '~/shared/utils/dateTime';

export const $allFiles = atom<FileRecord[]>([]);
export const $expandedDirectoryIds = atom<string[]>([]);

// 🟢🟢🟢 REMOTE DATA
export const $directoryList = directoriesQueryAtom();

type DirectoryListValue =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof $directoryList extends WritableAtom<infer V, any, any> ? V : never;

type DirectoriesByIdReadValue = Omit<DirectoryListValue, 'data'> & {
  data: Map<string, FileRecord>;
};

// 🟢🟢🟢 COMPUTED DATA
export const $directoriesById: Atom<DirectoriesByIdReadValue> = atom((get) => {
  const { data: directoryList = [], ...rest } = get($directoryList);

  const fileRecords =
    directoryList?.map(
      (node) =>
        ({
          id: node.id,
          name: node.fileName,
          type: 'Directory' as const,
          mimeType: 'application/x-directory',
          size: 0,
          parentId: node.parentId,
          path: node.path ? node.path.split('/').filter(Boolean) : [],
          createdAt: maybeParseZonedDateTime(node.timestamps.createdAt),
          updatedAt: maybeParseZonedDateTime(node.timestamps.updatedAt),
          createdBy: node.creator
            ? `${node.creator.firstName} ${node.creator.lastName}`
            : undefined,
          public: node.public,
          template: node.template,
          children: [],
        }) as FileRecord,
    ) ?? [];

  // Create a map to store all file records by ID for easy lookup and deduplication
  const fileRecordsById = new Map<string, FileRecord>();

  // First pass: deduplicate and create all file records, populate the map
  fileRecords.forEach((fileRecord) => {
    // Only add if not already present (deduplication by ID)
    if (!fileRecordsById.has(fileRecord.id)) {
      // Reset children array to ensure clean tree structure
      const cleanFileRecord = { ...fileRecord, children: [] };
      fileRecordsById.set(fileRecord.id, cleanFileRecord);
    }
  });

  return { ...rest, data: fileRecordsById };
});

export const $directoryTree: Atom<
  Omit<DirectoriesByIdReadValue, 'data'> & { data: FileRecord[] }
> = atom((get) => {
  const { data: directoriesById = new Map<string, FileRecord>(), ...rest } =
    get($directoriesById);

  const rootFiles: FileRecord[] = [];

  // First pass: build the tree structure
  directoriesById.forEach((fileRecord) => {
    if (fileRecord.parentId && directoriesById.has(fileRecord.parentId)) {
      // This file has a parent in our dataset, add it as a child
      const parent = directoriesById.get(fileRecord.parentId);
      fileRecord.parent = parent;
      parent?.children.push(fileRecord);
    } else {
      // This is a root file (no parent or parent not in dataset)
      rootFiles.push(fileRecord);
    }
  });

  // Second pass: sort children alphabetically for all directories
  directoriesById.forEach((fileRecord) => {
    fileRecord.children.sort((a, b) => a.name.localeCompare(b.name));
  });

  // Sort root files alphabetically
  rootFiles.sort((a, b) => a.name.localeCompare(b.name));

  return { ...rest, data: rootFiles };
});
